import { type ReactNode } from 'react';

// Renderiza o termo (Markdown enxuto: # ## ###, **bold**, listas com "- ", ---,
// tabelas, paragrafos) como HTML formatado. Evita dependencia de react-markdown —
// o template tem padroes limitados e conhecidos (ver termoRenderer.ts).
// Compartilhado entre o preview do aceite (RevisaoEAceiteEtapa) e o viewer publico
// do termo assinado (pages/TermoView).

function renderInline(text: string, keyBase: string): ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*\n]+\*)/g);
  return parts
    .filter((p) => p.length > 0)
    .map((part, idx) => {
      const key = `${keyBase}-${idx}`;
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={key} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
        return <em key={key} className="italic text-muted-foreground">{part.slice(1, -1)}</em>;
      }
      return <span key={key}>{part}</span>;
    });
}

function splitRow(line: string): string[] {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
}

export function TermoRendered({ markdown }: { markdown: string }) {
  const lines = markdown.split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  const isBlockBoundary = (line: string) => {
    const t = line.trim();
    return (
      t === '' ||
      t === '---' ||
      t.startsWith('# ') ||
      t.startsWith('## ') ||
      t.startsWith('### ') ||
      t.startsWith('- ') ||
      t.startsWith('|')
    );
  };

  while (i < lines.length) {
    const trimmed = lines[i].trim();

    if (trimmed === '') {
      i++;
      continue;
    }

    if (trimmed === '---') {
      blocks.push(<hr key={key++} className="my-5 border-border" />);
      i++;
      continue;
    }

    if (trimmed.startsWith('# ')) {
      blocks.push(
        <h1 key={key++} className="text-base font-bold uppercase tracking-wide text-brand-azul mt-6 mb-3 first:mt-0">
          {renderInline(trimmed.slice(2), `h1-${key}`)}
        </h1>,
      );
      i++;
      continue;
    }

    if (trimmed.startsWith('## ')) {
      blocks.push(
        <h2 key={key++} className="text-sm font-bold uppercase tracking-wide text-brand-azul mt-5 mb-2">
          {renderInline(trimmed.slice(3), `h2-${key}`)}
        </h2>,
      );
      i++;
      continue;
    }

    if (trimmed.startsWith('### ')) {
      blocks.push(
        <h3 key={key++} className="text-sm font-semibold text-foreground mt-4 mb-1.5">
          {renderInline(trimmed.slice(4), `h3-${key}`)}
        </h3>,
      );
      i++;
      continue;
    }

    if (trimmed.startsWith('- ')) {
      const items: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('- ')) {
        items.push(lines[i].trim().slice(2));
        i++;
      }
      blocks.push(
        <ul key={key++} className="list-disc pl-6 my-2 space-y-1">
          {items.map((item, idx) => (
            <li key={idx}>{renderInline(item, `li-${key}-${idx}`)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    if (
      trimmed.startsWith('|') &&
      i + 1 < lines.length &&
      /^\|[\s\-:|]+\|?$/.test(lines[i + 1].trim())
    ) {
      const headers = splitRow(lines[i]);
      i += 2; // skip header + separator
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        rows.push(splitRow(lines[i]));
        i++;
      }
      blocks.push(
        <div key={key++} className="my-3 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                {headers.map((h, idx) => (
                  <th
                    key={idx}
                    className="border border-border bg-muted px-3 py-2 text-left font-semibold"
                  >
                    {renderInline(h, `th-${key}-${idx}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rIdx) => (
                <tr key={rIdx}>
                  {row.map((cell, cIdx) => (
                    <td
                      key={cIdx}
                      className="border border-border px-3 py-2 align-top break-words"
                    >
                      {renderInline(cell, `td-${key}-${rIdx}-${cIdx}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // paragrafo: junta linhas consecutivas ate proxima fronteira de bloco
    const paraLines: string[] = [lines[i]];
    i++;
    while (i < lines.length && !isBlockBoundary(lines[i])) {
      paraLines.push(lines[i]);
      i++;
    }
    blocks.push(
      <p key={key++} className="my-2 text-justify">
        {renderInline(paraLines.join(' '), `p-${key}`)}
      </p>,
    );
  }

  return <>{blocks}</>;
}
